import { ISpinnerStyles, Spinner, SpinnerSize } from '@fluentui/react';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import * as React from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  ReadWriteAction,
  ComponentOverrides,
  ActionType,
  ParamOrVar,
  ScrollToBottomHandler,
  IRuntimeStaticConfig,
  ContentType,
  IntentGraph,
  IRuntimeHandlerConfig,
  ISAFActionExecutionResponse,
  LogEventName,
  SAFActionSchemaResponse,
  SAFContextVariable } from '@ems/yoda-ux';
import { httpUtils } from '../../../../web/common/apis/httpUtils';
import { DiagnosticReference } from '../../../../web/models/constants/getHelpConstants';
import { appContext } from '../../../../web/models/getHelp/appContext';
import { DafParameter } from '../../../../web/models/getHelp/DafParameter';
import { EmeraldUserType } from '../../../../web/models/getHelp/emeraldUserType';
import { GetHelpDiagnosticResponse } from '../../../../web/models/getHelp/GetHelpDiagnosticResponse';
import { getHelpLogger } from '../../../../web/utils/getHelpLogger';
import { getString } from '../../../../web/utils/stringLoader';
import { delay, tryParseUrl } from '../../../../web/utils/getHelputils';
import { captureContentUpdate, capturePageAction } from '../../../../web/utils/oneDsUtil';
import { isGetHelpSourceApp } from '../../../../web/utils/statelessUtil';
import {
  AlchemyInsightModel,
  AlchemyResourceRequestPayload,
  AlchemyResource,
  SAFActionRequestPayload,
  SAFActionExecutionRequestPayload,
} from '../../../models/apiModels';
import { BranchingTicketDataContext, Environment, SourceApps } from '../../../models/constants/constants';
import { TenantInfo } from '../../../models/reduxState';
import {
  addMarketToUrl,
  getAlchemyAppNameBySourceApp,
  wireupLinks,
  getDataBoundaryHeaderValue,
  addAppNamesToParam,
  addAlchemyPpeFlagToParam,
} from '../../../utilities';
import { GraphNode, LoggingHtmlAttribute, RaiseEventNames } from '../models/contactUsModels';
import { FreelanceRequest, RaveMetaData } from '../models/requestModels';
import { getHelpService } from '../services/getHelpServices';
import { getHostStyles, getSpinnerStyles } from '../styles/intentGraphStyles';

import { IStyledComponent } from '../styles/themesUI';
import { postAlchemyFeedbackAsync } from '../../../../web/common/apis/contactUs.api';

interface IIntentGraphProps {
  graphInsights: Array<AlchemyInsightModel>;
  appVer: string;
  sourceApp: SourceApps;
  market: string;
  areaName: string;
  isClientApp: boolean;
  redirectForSignInLink: string;
  correlationId: string;
  isSignedIn: boolean;
  safActionMaxRetries: number;
  environment: Environment;
  serviceRequestStarted: boolean;
  tenantInfo: TenantInfo;
  isThirdParty: boolean;
  deeplinkPath: string;
  dialogId: string;
  isBcqrRetrieved: boolean;
  isPostAlchemyFeedbackEnabled: boolean;

  // dispatchers
  dispatchSignInUser: () => void;
  dispatchRedirectForSignInLink: () => void;
  setUserIsSigningIntoClientApp: (status: boolean) => void;
  updateDraftRequest: (properties: Partial<FreelanceRequest>) => void;
  diagnosticStart: (nodeId: string, packageName: string) => void;
  diagnosticError: (result: string) => void;
  diagnosticTimeout: () => void;
  dispatchUpdateDialogId: (dialogId: string) => void;
}

type ISelfHelpIntentGraphProps = IIntentGraphProps & IStyledComponent<Partial<ISpinnerStyles>>;

export class SelfHelpIntentGraph extends React.Component<ISelfHelpIntentGraphProps> {
  private raisedEventName: string;
  private key = uuidv4();
  private eventUId: string;
  public render() {
    const payload =
      this.props.graphInsights &&
      this.props.graphInsights.length > 0 &&
      this.props.graphInsights[0].Description;
    return payload ? (
      <IntentGraph
        key={this.key}
        staticConfig={this.getRunTimeStaticConfig()}
        callbackHandlers={this.getcallbackHandlers()}
      />
    ) : null;
  }

  public componentDidMount = () => {
    if (!this.props.redirectForSignInLink && !this.props.isThirdParty) {
      this.props.dispatchRedirectForSignInLink();
    }
  };

  public componentDidUpdate = (prevProps: ISelfHelpIntentGraphProps) => {
    if (this.props.serviceRequestStarted) {
      // get context data before Yoda dismounts
      this.setBranchingTicketData();
    }
    // currently raising event for signin only
    if (
      this.raisedEventName &&
      !prevProps.isSignedIn &&
      this.props.isSignedIn
    ) {
      this.onRaisedEventCompleted(this.raisedEventName, true);
    }

    if (prevProps.theme !== this.props.theme) {
      window?.yoda?.IntentGraph?.Events?.onThemeChanged(this.props.theme);
    }
  };

  public componentWillUnmount = () => {
    // get context data before Yoda dismounts
    this.setBranchingTicketData();
  };

  public browserSignInClickHandler = () => {
    if (this.props.sourceApp === SourceApps.SurfaceApp || this.props.sourceApp === SourceApps.SurfaceApp2) {
      window.open(
        this.props.redirectForSignInLink,
        '_self'
      );
    } else {
      window.open(
        this.props.redirectForSignInLink,
        '_blank',
        'width=500,height=700'
      );
    }
    this.props.dispatchSignInUser();
  };

  public clientAppSignInClickHandler = () => {
    this.props.setUserIsSigningIntoClientApp(true);
  };

  public signInClickHandler = (raisedEvent?: boolean) => {
    if (!this.props.isThirdParty) {
      const logHandlerTags = {
        content: {
          contentName: 'intentGraphSignIn',
          correlationId: this.props.correlationId,
          formnm: 'maxFunnel',
          'data-bi-bhvr': 'SIGNIN',
          fileName: 'SelfHelpIntentGraph.tsx',
          methodName: 'signInClickHandler',
          areaName: this.props.areaName,
          raisedEvent: !!raisedEvent,
        },
        behavior: 'SIGNIN',
      };
      capturePageAction(null, logHandlerTags);

      this.props.isClientApp
        ? this.clientAppSignInClickHandler()
        : this.browserSignInClickHandler();
    }
  };

  private raiseEventHandler = (
    eventName: string,
    _eventArgs?: string,
    uId?: string
  ): boolean => {
    switch (eventName.toLowerCase()) {
      case RaiseEventNames.FeedbackCamera:
        window.open('feedback-hub://?searchString=camera', '_blank');
        return true;
      case RaiseEventNames.SignIn.toLowerCase():
        // handle signIn
        this.raisedEventName = eventName;
        this.eventUId = uId;
        if (this.isUserSignedIn()) {
          this.onRaisedEventCompleted(this.raisedEventName, true);
        } else {
          this.signInClickHandler(true);
        }
        return true;
      case RaiseEventNames.ContactSupport.toLowerCase():
        switch (appContext.emeraldUserType) {
          case EmeraldUserType.Consumer:
            if (!appContext.appSupportsAuth) {
              notifyParent(`{"command" : "OpenSkylightPane"}`);
            }
            break;
          case EmeraldUserType.Commercial:
            document?.getElementById('commercialContainer')?.scrollIntoView();
            break;
        }
        return true;
      default:
        break;
    }
    return false;
  };

  private getComponent = (componentName: ComponentOverrides): React.ComponentType<any> => {
    if (!componentName) {
      return null;
    }
    const components: {[key: string]: React.ReactNode} = {
      [ComponentOverrides.DiagnosticProgressBar]: (<Spinner
        size={SpinnerSize.medium}
        styles={getSpinnerStyles()}
      />),
    };
    return (components[componentName] || null) as unknown as React.ComponentType<any>;
  };

  private isUserSignedIn = (): boolean => {
    return this.props.isSignedIn;
  };

  // tslint:disable-next-line: no-any
  private logHandler = (
    eventName: string,
    logLevel: string,
    sessionId: string,
    message: string,
    adhocs: any[]
  ) => {
    message = `message: ${message}, sessionId: ${sessionId}`;
    let behavior = 160;
    if (eventName) {
      let tagId;
      let isPageAction = false;
      switch (eventName.toLowerCase()) {
        case LogEventName.onChoiceClick.toLowerCase():
          message = 'onChoiceClick -' + message;
          tagId = 506176;
          // behavior click
          behavior = 281;
          isPageAction = true;
          break;
        case LogEventName.onComponentError.toLowerCase():
          message = 'onComponentError -' + message;
          tagId = 506177;
          break;
        case LogEventName.onComponentRenderd.toLowerCase():
          message = 'onComponentRenderd -' + message;
          tagId = 506178;
          break;
        case LogEventName.onContentRetrievalError.toLowerCase():
          message = 'onContentRetrievalError -' + message;
          tagId = 506179;
          break;
        case LogEventName.onJsonDocumentValidationError.toLowerCase():
          message = 'onJsonDocumentValidationError -' + message;
          tagId = 506342;
          break;
        case LogEventName.onSAfSchemaFetchFailed.toLowerCase():
          message = 'onSAfSchemaFetchFailed -' + message;
          tagId = 508525;
          break;
        case LogEventName.onSAfActionRunFailed.toLowerCase():
          message = 'onSAfActionRunFailed -' + message;
          tagId = 508526;
          break;
        case LogEventName.onRelatedDialogFetchFailed.toLowerCase():
          message = 'onRelatedDialogFetchFailed -' + message;
          tagId = 511966;
          break;
        case LogEventName.onRedirectNodeRendered.toLowerCase():
          message = 'onRedirectNodeRendered -' + message;
          tagId = 511967;
          break;
        case LogEventName.onRedirectNodeTargetDialogIdMissing.toLowerCase():
          message = 'onRedirectNodeTargetDialogIdMissing -' + message;
          tagId = 511968;
          break;
        case LogEventName.onRelatedDialogFetchSuccess.toLowerCase():
          message = 'onRelatedDialogFetchSuccess -' + message;
          tagId = 511969;
          break;
        default:
          break;
      }

      if (tagId) {
        const oMessage = {
          Message: message,
          AdHocs: adhocs,
        };
        const logHandlerTags = {
          content: {
            contentName: 'intentGraphEventLogs',
            correlationId: this.props.correlationId,
            tagId: tagId,
            formnm: 'maxFunnel',
            eventName: eventName,
            fileName: 'SelfHelpIntentGraph.tsx',
            methodName: 'logHandler',
            areaName: this.props.areaName,
            message: encodeURIComponent(JSON.stringify(oMessage)),
          },
          behavior: behavior,
        };
        if (isPageAction) {
          capturePageAction(null, logHandlerTags);
        } else {
          captureContentUpdate(logHandlerTags);
        }
      }
    }

    getHelpLogger.logIntentGraphLogs(eventName, logLevel, message, adhocs);
    if (eventName === LogEventName.onDafDiagnosticsActionTimeout || eventName === LogEventName.onGetDiagnosticsResultTimeout) {
      this.props.diagnosticTimeout();
    }
  };

  private getcallbackHandlers = (): IRuntimeHandlerConfig => {
    const callbackHandlers: IRuntimeHandlerConfig = {
      LogHandler: this.logHandler,
      ContentRequestHandler: this.contentRequestHandler,
      NodeRendered: this.onNodeRenderd,
      RaiseEventHandler: this.raiseEventHandler,
      GetSAFActionSchema: this.getAlchemySAFActionSchema,
      ExecuteSAFAction: this.executeAlchemySAFAction,
      IsUserSignedIn: this.isUserSignedIn,
      GetComponent: this.getComponent,
      DiagnosticsActionHandler: this.runDiagnostics,
      IsInitialized: this.isInitializedHandler,
      scrollToBottomHandler: isGetHelpSourceApp(this.props.sourceApp) ? this.getHelpScrollToBottomHandler : undefined,
    };
    return callbackHandlers;
  };

  private onRaisedEventCompleted = (
    eventName: string,
    succeeded: boolean
  ): void => {
    if (
      !!window.yoda &&
      !!window.yoda.IntentGraph &&
      !!window.yoda.IntentGraph.Events &&
      typeof window.yoda.IntentGraph.Events.onRaisedEventCompleted ===
      'function'
    ) {
      // notify yoda of event completion
      window.yoda.IntentGraph.Events.onRaisedEventCompleted(
        eventName,
        succeeded,
        this.eventUId
      );
    }
  };

  private logError = (errorMessage: string, methodName: string): void => {
    const errorTags = {
      content: {
        contentName: 'intentGraphEventLogs',
        correlationId: this.props.correlationId,
        formnm: 'maxFunnel',
        fileName: 'SelfHelpIntentGraph.tsx',
        methodName: methodName,
        areaName: this.props.areaName,
        message: encodeURIComponent(errorMessage),
      },
      behavior: 300,
    };
    captureContentUpdate(errorTags);
  };

  private getLoggingAttributes = () => {
    const insight = this.props.graphInsights
      ? this.props.graphInsights[0]
      : null;
    const bucketId = insight && insight.BucketId;
    const loggingAttributes: {[key: string]: {[key: string]: LoggingHtmlAttribute}} = {
      base: {
        'data-bi-area': { Name: 'data-bi-area', Value: this.props.areaName },
        'data-bi-formnm': { Name: 'data-bi-formnm', Value: 'maxFunnel' },
        'data-bi-name': { Name: 'data-bi-name', Value: !!bucketId ? bucketId.toString() : '' },
      },
      unauthbranchingcontactsupport: {
        'id': { Name: 'id', Value: 'unAuthBranchingContactSupport' },
        'data-bi-bhvr': { Name: 'data-bi-bhvr', Value: 'CONTACT' },
        'data-bi-isUnauthBranchingContactSupport': { Name: 'data-bi-isUnauthBranchingContactSupport', Value: 'true' },
      },
      unauthbranchingsignin: {
        'id': { Name: 'id', Value: 'unAuthBranchingSignIn' },
        'data-bi-bhvr': { Name: 'data-bi-bhvr', Value: 'SIGNIN' },
        'data-bi-unAuthBranchingSignIn': { Name: 'data-bi-unAuthBranchingSignIn', Value: 'true' },
      },
    };
    return loggingAttributes;
  };

  private contentRequestHandler = async (
    contentType: string,
    contentId: string,
    parentDialogId?: string
  ): Promise<string> => {
    let errorMessage = '';
    const methodName = 'contentRequestHandler';

    if (!contentType || !contentId) {
      errorMessage = `content retrieval failure- contentType: ${contentType}, contentId: ${contentId}`;
      this.logError(errorMessage, methodName);
      return String();
    }

    const q = new AlchemyResourceRequestPayload();
    q.Locale = this.props.market;
    q.SolutionType = contentType;
    q.SolutionId = contentId;
    q.CorrelationId = this.props.correlationId;
    const alchemyAppName = getAlchemyAppNameBySourceApp(this.props.sourceApp);
    if (!!alchemyAppName) {
      q.App = alchemyAppName;
    }

    const destinationUrl = addMarketToUrl('/api/contactus/v1/GetAlchemyResource', this.props.market);
    const dataBoundary = getDataBoundaryHeaderValue(this.props.tenantInfo);
    const params = {};
    addAppNamesToParam(params, this.props.appVer, this.props.sourceApp);
    addAlchemyPpeFlagToParam(params);
    const config: AxiosRequestConfig = {
      params: params,
    };

    try {
      const result: AxiosResponse<AlchemyResource> = await httpUtils.post(
        destinationUrl,
        q,
        { 'X-Ms-DataBoundary': dataBoundary },
        config,
        true
      );

      if (result.data) {
        const response = result.data;
        if (contentType.toLowerCase() !== ContentType.Branching.toLowerCase()) {
          // add attributes for logging
          response.SolutionContent = wireupLinks(
            response.SolutionContent,
            this.getLoggingAttributes(),
            undefined,
            parentDialogId,
            contentType,
            contentId
          );
        }
        if (this.props.dialogId !== parentDialogId) {
          this.props.dispatchUpdateDialogId(parentDialogId);
        }
        getHelpLogger.logAlchemyMultiTurnContentResponseReceived(contentType, contentId, parentDialogId, result.data);
        return JSON.stringify(response);
      }

      errorMessage = `content retrieval failure- responseStatus:${(result && result.status) || null}`;
    } catch (error) {
      const message = error.message;
      const stack = error.stack;
      errorMessage = `content retrieval failure- message: ${message}, stack: ${stack}`;
      this.logError(errorMessage, methodName);
      getHelpLogger.logAlchemyMultiTurnContentError(error);
    }

    this.logError(errorMessage, methodName);
    return String();
  };

  private updateInitialVariableData = async () => {
    // Logic to write deeplink initial variables intos IntentGraph
    // Here is an example of deeplink path:
    // "ms-contact-support://settings/SettingsPageDevices?SearchKey=Audio%20Issues&Dialog_param1=1234&Dialog_param2=5678",
    // we extract the keys that start with 'dialog_', and set into initialVariables of json after removing the dialog_ prefix
    // Note: If author set initialVariables in json and its not null, then we wont override it
    const prefixIdentifier = 'dialog_';
    if (this.props.deeplinkPath) {
      const [isValidUrl, url] = tryParseUrl(this.props.deeplinkPath);
      if (isValidUrl && !!url.searchParams) {
        const params = (url.searchParams as URLSearchParams).entries();
        const paramsFilteredForDialog = Array.from(params)
          .filter(
            // tslint:disable-next-line:typedef
            ([key], i) =>
              key.toLowerCase().startsWith(prefixIdentifier) && // any key that starts with DIALOG_ or dialog_ or mix of case
              key.length > prefixIdentifier.length && // any that had more charcters than just 'dialog_'
              i === Array.from((url.searchParams as URLSearchParams).keys()).indexOf(key) // key which did occur in array earlier
          )
          // tslint:disable-next-line:typedef
          .map(([key, value]) => {
            return { name: key.slice(prefixIdentifier.length), value: value }; // remove the 'dialog_' prefix from the key
          });

        if (window.yoda?.IntentGraph?.Events?.onReadWrite) {
          // Add to existing initial variables in json pool.
          await window.yoda.IntentGraph.Events.onReadWrite({
            actionType: ReadWriteAction.WriteGlobalVariables,
            parameters: paramsFilteredForDialog,
          });
        }
      }
    }

    // Logic to read initial variables from IntentGraph, even if we did not write initial variables, Dialog JSON can still have initial variables that came with dialog
    // Please note: only initial variables hardcoded in JSON and initial variable updated by harness into JSON are collected here.
    // If App was launched with deeplinks which are not meant for JSON initial variables, deeplinks are not sent as data to ticket.
    if (window.yoda?.IntentGraph?.Events?.onReadWrite) {
      const dataToPass: RaveMetaData[] = [];
      const initialData: { key: string, value: string }[] = await window.yoda.IntentGraph.Events.onReadWrite({
        actionType: ReadWriteAction.ReadGlobalVariables,
      });

      if (!!initialData && Object.keys(initialData)?.length > 0) {
        for (const [key, val] of Object.entries(initialData)) {
          if (!!key && !!val) {
            dataToPass.push({ Key: key + '(initial_data)', Value: val.toString() });
          }
        }

        this.props.updateDraftRequest({
          MetaDatas: dataToPass,
        });
      }
    }
  };

  private isInitializedHandler = async () => {
    await this.updateInitialVariableData();
  };

  private onNodeRenderd = (
    metaId: string,
    title: string,
    _team: string,
    nodeId: string,
    outcomeSignal: string,
    tags?: string,
    nodeType?: string,
    text?: string | null,
    nodeLogProperties?: string
  ): void => {
    // Store/Update metaId only if it is different from previous one
    if (this.props.dialogId !== metaId) {
      this.props.dispatchUpdateDialogId(metaId);
    }

    if (this.props.isPostAlchemyFeedbackEnabled) {
      postAlchemyFeedbackAsync({
        IsInsightRetrieved: !!this.props.graphInsights?.length,
        IsBcqrRetrieved: this.props.isBcqrRetrieved,
        PerformanceResolutionRate: outcomeSignal,
      });
    }

    const oMessage = {
      metaId: metaId,
      title: title,
      nodeId: nodeId,
      outcomeSignal: outcomeSignal,
      tags: tags,
      nodeType: nodeType,
      text: text,
      nodeLogProperties: nodeLogProperties,
    };
    const logHandlerTags = {
      content: {
        contentName: 'intentGraphNodeRenderedLogs',
        correlationId: this.props.correlationId,
        tagId: 506274,
        formnm: 'maxFunnel',
        fileName: 'SelfHelpIntentGraph.tsx',
        methodName: 'onNodeRenderd',
        areaName: this.props.areaName,
        message: encodeURIComponent(JSON.stringify(oMessage)),
      },
      behavior: 160,
    };
    captureContentUpdate(logHandlerTags);
    getHelpLogger.logIntentGraphNodeRendered(metaId, nodeId, outcomeSignal, nodeType, nodeLogProperties);
  };

  private _addAtributesToSchema = (payload: string) => {
    try {
      const schema = JSON.parse(payload);
      const loggingAttributes = this.getLoggingAttributes();
      if (schema) {
        const dialogId: string = schema.meta.id;
        schema.nodes.forEach((node: GraphNode) => {
          const nodeId: string = node.id;
          if (
            node.text &&
            node.text.content &&
            node.text.contentType &&
            node.text.contentType.toLowerCase() ===
            ContentType.EmbeddedHtml.toLowerCase()
          ) {
            node.text.content = wireupLinks(node.text.content, loggingAttributes, nodeId, dialogId);
          }
        });
      }
      return schema;
    }
    catch (error) {
      this.logError(error.message, this._addAtributesToSchema.name);
      return null;
    }
  };

  private getAlchemySAFActionSchema = async (
    actionId: string
  ): Promise<SAFActionSchemaResponse | null> => {
    let errorMessage = '';
    const methodName = 'getAlchemySAFActionSchema';

    if (!actionId) {
      errorMessage = `saf schema fetch failure- actionId is ${actionId}`;
      this.logError(errorMessage, methodName);
      return null;
    }

    const q = new SAFActionRequestPayload();
    q.Locale = this.props.market;
    q.ActionId = actionId;
    q.CorrelationId = this.props.correlationId;
    const alchemyAppName = getAlchemyAppNameBySourceApp(this.props.sourceApp);
    if (!!alchemyAppName) {
      q.App = alchemyAppName;
    }

    const destinationUrl = addMarketToUrl('/api/contactus/v1/GetAlchemySAFActionSchema', this.props.market);
    const dataBoundary = getDataBoundaryHeaderValue(this.props.tenantInfo);
    const params = {};
    addAppNamesToParam(params, this.props.appVer, this.props.sourceApp);
    addAlchemyPpeFlagToParam(params);
    const config: AxiosRequestConfig = {
      params: params,
    };

    try {
      const response: AxiosResponse<SAFActionSchemaResponse> = await httpUtils.post(
        destinationUrl,
        q,
        { 'X-Ms-DataBoundary': dataBoundary },
        config,
        true
      );

      if (response.data) {
        return response.data;
      }

      errorMessage = `saf schema fetch failure- responseStatus:${response.status || null}`;
    } catch (error) {
      const message = error.message;
      const stack = error.stack;
      errorMessage = `saf schema fetch failure- message: ${message}, stack: ${stack}`;
    }

    this.logError(errorMessage, methodName);
    return null;
  };

  private safParametersEnhancer = (
    parameters: object,
    contextVariables: SAFContextVariable[]
  ): void => {
    if (contextVariables) {
      contextVariables.forEach((v: SAFContextVariable, index: number) => {
        if (v && v.Name) {
          switch (v.Name.toLowerCase()) {
            case 'locale':
              parameters[v.Name] = this.props.market;
              contextVariables.splice(index, 1);
          }
        }
      });
    }
  };

  private executeAlchemySAFAction = async (
    actionId: string,
    parameters: object,
    contextVariables: SAFContextVariable[]
  ): Promise<ISAFActionExecutionResponse | null> => {
    getHelpLogger.logExecuteAlchemySAFActionInvoked(this.props.correlationId, actionId);
    let errorMessage = '';
    const methodName = 'startSafExecution';

    if (!parameters) {
      errorMessage = `saf execution failure- parameters is ${parameters}`;
      getHelpLogger.logGetSAFExecutionResponseFailed(errorMessage, actionId);
      this.logError(errorMessage, methodName);
      return null;
    }

    const q = new SAFActionExecutionRequestPayload();
    this.safParametersEnhancer(parameters, contextVariables);
    q.Locale = this.props.market;
    q.Parameters = parameters;
    q.ActionId = actionId;
    q.CorrelationId = this.props.correlationId;
    q.ContextVariables = contextVariables;
    q.V2 = true;
    const alchemyAppName = getAlchemyAppNameBySourceApp(this.props.sourceApp);
    if (!!alchemyAppName) {
      q.App = alchemyAppName;
    }
    const destinationUrl = addMarketToUrl('/api/contactus/v1/ExecuteAlchemySAFAction', this.props.market);
    const dataBoundary = getDataBoundaryHeaderValue(this.props.tenantInfo);
    const params = {};
    addAppNamesToParam(params, this.props.appVer, this.props.sourceApp);
    addAlchemyPpeFlagToParam(params);
    const config: AxiosRequestConfig = {
      params: params,
    };

    try {
      const response: AxiosResponse<ISAFActionExecutionResponse> = await httpUtils.post(
        destinationUrl,
        q,
        {
          Accept: 'application/json;odata.metadata=none',
          'Content-Type': 'application/json',
          'X-Ms-DataBoundary': dataBoundary,
        },
        config,
        true
      );

      if (response.data) {
        return response.data;
      }

      errorMessage = `saf execution failure- responseStatus:${response.status || null}`;
    } catch (error) {
      const message = error.message;
      const stack = error.stack;
      errorMessage = `saf execution failure- message: ${message}, stack: ${stack}`;
    }

    getHelpLogger.logGetSAFExecutionResponseFailed(errorMessage, actionId);
    this.logError(errorMessage, methodName);
    return null;
  };

  private setBranchingTicketData = async () => {
    if (
      window.yoda &&
      window.yoda.IntentGraph &&
      window.yoda.IntentGraph.Events &&
      window.yoda.IntentGraph.Events.onReadWrite
    ) {
      await window.yoda.IntentGraph.Events.onReadWrite({
        actionType: ReadWriteAction.ReadContextVariables,
        context: BranchingTicketDataContext,
      }).then((ticketData: { key: string, value: string }[]) => {
        // yoda sends lowercase key value property and we map to Pascalcase to match Rave
        const ticketDataArray: RaveMetaData[] = [];
        if (ticketData && ticketData.length > 0) {
          ticketData.forEach((metaData: {key: string, value: string}) => {
            ticketDataArray.push({ Key: metaData.key, Value: metaData.value });
          });
          this.props.updateDraftRequest({
            MetaDatas: ticketDataArray,
          });
        }
      }).
      catch((error: Error) => {
        const logHandlerTags = {
          content: {
            contentId: 'errorGettingBranchingContext',
            contentName: 'errorGettingBranchingContext',
            context: BranchingTicketDataContext,
            formnm: 'maxFunnel',
            methodName: 'setBranchingTicketData',
            areaName: 'modalitiesSection',
            errorMessage: error ? error.message : '',
            errorStack: error ? error.stack : '',
          },
          behavior: 'Other',
        };
        capturePageAction(null, logHandlerTags);
      });
    }
  };

  private runDiagnostics = async (
    nodeId: string,
    actionType: ActionType,
    actionReference: string,
    timeoutInSecond: number,
    parameters?: ParamOrVar[]
  ) => {
    // logging
    // const loggingRequest: GetHelpLoggingRequest = {
    //   TagId: TagId.RunDiagnosticInvoked,
    //   LogLevel: LogLevel.Info,
    //   Message: `DiagnosticsActionHandler in harness invoked`,
    //   Adhoc0: locale,
    //   Adhoc1: nodeId,
    //   Adhoc2: actionType,
    //   Adhoc3: actionReference,
    //   Adhoc4: !!parameters ? parameters.length.toString() : '0',
    //   Adhoc5: EmeraldUserType[appContext.emeraldUserType]
    // };
    // await getHelpService.logEventAsync(loggingRequest);

    this.props.diagnosticStart(nodeId, actionReference);

    // Fire function createDiagnosticObject to create the Diagnostic Object

    let sessionResponse: GetHelpDiagnosticResponse = null;
    let diagnosticContext = {};
    let parameterLength = 0;
    const params: DafParameter[] = [];

    if (parameters !== null) {
      parameterLength = parameters.length;
      for (let i = 0; i < parameterLength; i++) {
        const element: DafParameter = { parameterName: parameters[i].name, parameterValue: parameters[i].value };
        params.push(element);
      }
    }

    switch (actionType) {
      case ActionType.DafV1:
      case ActionType.DafV1WithParams:
        sessionResponse = await getHelpService.getDiagnosticSessionAsync(DiagnosticReference.ProblemKey, actionReference);
        if (sessionResponse !== null) {
          params.push({ parameterName: 'GH_ActionRef', parameterValue: actionReference });
          params.push({ parameterName: 'GH_SessionId', parameterValue: appContext.sessionId });
          params.push({ parameterName: 'GH_CV', parameterValue: appContext.correlationVector });
          params.push({ parameterName: 'GH_DAF_SessionId', parameterValue: sessionResponse.SessionId });
          params.push({ parameterName: 'GH_DAF_AutohealId', parameterValue: sessionResponse.AutohealId });
          diagnosticContext = {
            sessionId: sessionResponse.SessionId,
            autohealId: sessionResponse.AutohealId,
            parameters: params,
            parameterCount: parameterLength + 5,
          };
        } else {
          this.props.diagnosticError('Could not get DiagnosticResult from diagnosticSession API');
          return;
        }
        break;
      case ActionType.DafV2:
      case ActionType.DafV2WithParams:
        diagnosticContext = {
          actionId: appContext.sessionId,
          mitigationId: actionReference,
          parameters: params,
          parameterCount: parameterLength,
        };
        break;
    }

    // Temporary mitigation, add an artificial 1s delay before we send the signal to UWP
    // in case the session is not ready when DiagSvc is trying to get SAS url to download
    // the diag package.
    await delay(500);
    // Signal UWP to run diagnostic package
    notifyParent(JSON.stringify({
      command: 'RunDiagnostic',
      context: diagnosticContext,
    }));
  };

  private getHelpScrollToBottomHandler: ScrollToBottomHandler = (lastMessage: HTMLElement) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const scrollablePane: HTMLDivElement = document
      .getElementById('GetHelpScrollablePane')
      ?.querySelector('[data-is-scrollable]');

    if (!scrollablePane) {
      return;
    }

    // 60 is the footer height
    const footerOffset = appContext.appSupportsAuth ? 60 : 0;

    let currentNodeBottomPosition = lastMessage.getBoundingClientRect().bottom;
    const nodeHeight = lastMessage.getBoundingClientRect().height;

    const longNode = nodeHeight > scrollablePane.clientHeight - footerOffset;

    const int = setInterval(() => {
      // Detect if user scroll-up or if user is typing in a dialog input box, then stop auto scroll
      if (
        lastMessage.getBoundingClientRect().bottom > currentNodeBottomPosition ||
        (document.activeElement.tagName === 'INPUT' && document.activeElement.getAttribute('name') !== 'Search Box')
      ) {
        clearInterval(int);
        return;
      }
      if (longNode) {
        // for long node, just put the top of the node at 150px from top of the window
        if (lastMessage.getBoundingClientRect().top <= 150) {
          clearInterval(int);
        }
      } else {
        if (
          lastMessage.getBoundingClientRect().bottom + footerOffset <=
          scrollablePane.getBoundingClientRect().bottom
        ) {
          clearInterval(int);
        }
      }
      scrollablePane.scrollTop = scrollablePane.scrollTop + 4;
      currentNodeBottomPosition = lastMessage.getBoundingClientRect().bottom;
    }, 1);
  };

  private getRunTimeStaticConfig = () => {
    const isGetHelp = isGetHelpSourceApp(this.props.sourceApp);
    const insight = this.props.graphInsights[0];
    const payload = insight.Description;
    const runtimeStaticConfig: IRuntimeStaticConfig = {
      hostAppName: this.props.appVer || this.props.sourceApp,
      locale: this.props.market,
      prefix: 'smc_',
      // SMC doesn't have sessionId
      sessionId: this.props.correlationId,
      correlationId: this.props.correlationId,
      payload: payload ? this._addAtributesToSchema(payload!) : null,
      customstyle: getHostStyles(this.props.theme, this.props.environment, this.props.sourceApp),
      enableScrollToTop: !isGetHelp,
      enableScrollToBottom: isGetHelp,
      namespace: this.props.areaName,
      safDiagnosticsSettings: {
        safActionMaxRetries: this.props.safActionMaxRetries,
      },
      theme: this.props.theme,
      timeoutInSeconds: 300,
      localizedResources: {
        "EditIconAriaLabel": getString('SelfHelp_IntentGraph_EditIconAriaLabel')
      }
    };
    return runtimeStaticConfig;
  };
}
